/**
 * Step3Project — sélection ou création du projet (9.T.5.v3.6).
 *
 * Selon strategy :
 *   - attach_existing_project → ProjectPicker (affiche compagnie + code — QA #1)
 *   - autres → form new_project (title + insurer_codes facultatif — QA #3)
 *
 * QA #2 v3.6 post-QA — titre projet suggéré depuis
 * `classification.candidate_company.legal_name` si disponible. Le broker peut
 * modifier avant submit. Aucune génération arbitraire si aucune donnée fiable.
 * QA #3 — `insurer_codes` facultatif ici. Devient obligatoire uniquement Step 5
 * si toggle `create_quote_request` activé (registre `target_insurers.min(1)`).
 */

import type { ClassificationPayload } from "@kollectivia/shared";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { InsurerCatalogBoundary } from "../../../../components/InsurerCatalogBoundary";
import { ProjectPicker } from "../../../../components/ProjectPicker";
import { useInsurerCatalog } from "../../../../lib/queries/insurers";

import type { WizardData } from "./MaterializeWizard";

type Props = {
  data: WizardData;
  onChange: (patch: Partial<WizardData>) => void;
  classification?: ClassificationPayload | null;
};

/**
 * Extrait un titre projet suggéré depuis la classification IA.
 * QA #2 : suggestion réelle SEULEMENT si `candidate_company.legal_name` existe.
 * Aucune valeur arbitraire type "Projet YYYY-MM-DD".
 */
function extractSuggestedTitle(cls: ClassificationPayload | null | undefined): string | null {
  if (!cls) return null;
  const maybe = (cls as unknown as { candidate_company?: { legal_name?: string } })
    .candidate_company;
  if (maybe?.legal_name && maybe.legal_name.trim().length > 0) {
    // Convention : « Renouvellement — <legal_name> » comme suggestion neutre.
    return `Renouvellement — ${maybe.legal_name.trim()}`;
  }
  return null;
}

export function Step3Project({ data, onChange, classification }: Props) {
  const { t } = useTranslation();
  const insurerCatalog = useInsurerCatalog();

  // QA #2 — hydratation one-shot du titre proposé si aucun titre saisi.
  // Idempotent : ne re-écrase pas les valeurs déjà saisies par le broker.
  const suggestedTitle = extractSuggestedTitle(classification ?? null);
  useEffect(() => {
    if (!suggestedTitle) return;
    if (data.newProject?.title) return;
    if (data.strategy === "attach_existing_project") return;
    onChange({
      newProject: {
        ...(data.newProject ?? {}),
        title: suggestedTitle,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedTitle, data.strategy]);

  if (data.strategy === "attach_existing_project") {
    return (
      <section>
        <h3 className="mb-1 text-lg font-medium text-slate-900">
          {t("intake_events.detail.materialize.wizard.step_3_project.title")}
        </h3>
        <p className="mb-4 text-sm text-slate-600">
          {t("intake_events.detail.materialize.wizard.step_3_project.subtitle")}
        </p>
        <label className="mb-1 block text-xs font-medium text-slate-600">
          {t("intake_events.detail.materialize.wizard.step_3_project.attach_project_label")}
        </label>
        <ProjectPicker
          value={data.existingProjectId}
          onChange={(id) => onChange({ existingProjectId: id ?? undefined })}
        />
      </section>
    );
  }

  // Both create branches share the same form
  const toggleInsurer = (code: string) => {
    const current = data.newProject?.insurer_codes ?? [];
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    onChange({
      newProject: {
        ...(data.newProject ?? {}),
        insurer_codes: next,
      },
    });
  };

  return (
    <section>
      <h3 className="mb-1 text-lg font-medium text-slate-900">
        {t("intake_events.detail.materialize.wizard.step_3_project.title")}
      </h3>
      <p className="mb-4 text-sm text-slate-600">
        {t("intake_events.detail.materialize.wizard.step_3_project.subtitle")}
      </p>

      <div className="space-y-3">
        <div>
          <label htmlFor="new-project-title" className="block text-xs font-medium text-slate-600">
            {t("intake_events.detail.materialize.wizard.step_3_project.new_project_title_label")}
          </label>
          <input
            id="new-project-title"
            type="text"
            maxLength={200}
            value={data.newProject?.title ?? ""}
            onChange={(e) =>
              onChange({
                newProject: {
                  ...(data.newProject ?? {}),
                  title: e.target.value,
                },
              })
            }
            className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 w-full rounded border border-slate-300 p-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>

        <fieldset>
          <legend className="mb-1 text-xs font-medium text-slate-600">
            {t("intake_events.detail.materialize.wizard.step_3_project.new_project_insurers_label")}
          </legend>
          <InsurerCatalogBoundary catalog={insurerCatalog}>
            {(catalog) => (
              <div className="flex flex-wrap gap-2">
                {catalog.activeOptions.map((ins) => {
                  const active = (data.newProject?.insurer_codes ?? []).includes(ins.value);
                  return (
                    <button
                      key={ins.value}
                      type="button"
                      onClick={() => toggleInsurer(ins.value)}
                      aria-pressed={active}
                      className={
                        active
                          ? "border-brand-500 bg-brand-100 text-brand-800 focus-visible:ring-brand-500 rounded-full border px-3 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2"
                          : "focus-visible:ring-brand-500 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
                      }
                    >
                      {ins.label}
                    </button>
                  );
                })}
              </div>
            )}
          </InsurerCatalogBoundary>
        </fieldset>
      </div>
    </section>
  );
}
